import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { ArrowBackIcon } from "../../shared/components/icons";
import { ContainerMetaChips } from "../../shared/components/ContainerMetaChips";
import { IdentityLine } from "../../shared/components/IdentityLine";
import { useT } from "../../shared/i18n";
import { COMPACT_PAGE_TITLE_SIZE } from "../../theme";
import type { BackTarget } from "./back";
import type { ContentHeaderModel, Crumb } from "./contentHeader";

/**
 * The in-content page header (spec §3): text back link, one `h1` title and a
 * crumb strip. The phone renders the compact variant (smaller title, collapsed
 * crumbs); tablet/desktop get the full trail.
 */
export function ContentHeader({
  model,
  compact,
  linkLastCrumb = false,
  onBack,
  onCrumb,
}: {
  model: ContentHeaderModel | null;
  /** Phone compact geometry: 1.3rem title, one-line collapsed crumbs. */
  compact: boolean;
  /**
   * Make the last crumb a tap target too. Container views end at the view
   * itself, so the last crumb is plain text; item detail ends at its owning
   * container, which is one navigation step away (spec §3).
   */
  linkLastCrumb?: boolean;
  onBack: (target: BackTarget) => void;
  /** Ancestor crumb navigation: a container id, or null for the root. */
  onCrumb: (containerId: string | null) => void;
}) {
  const t = useT();
  if (!model) return null;

  return (
    <Box component="header" data-testid="content-header" sx={{ mb: 2 }}>
      {model.back && (
        <Button
          variant="text"
          size="small"
          color="inherit"
          startIcon={<ArrowBackIcon fontSize="small" />}
          onClick={() => onBack(model.back!)}
          aria-label={t(model.back.labelKey)}
          data-testid="content-back"
          sx={{ minHeight: 44, px: 0.5, mb: 0.5, color: "text.secondary", fontWeight: 600 }}
        >
          {t(model.back.labelKey)}
        </Button>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography
          component="h1"
          variant="h1"
          tabIndex={-1}
          data-testid="content-title"
          sx={{ m: 0, flexGrow: 1, minWidth: 0, ...(compact ? { fontSize: COMPACT_PAGE_TITLE_SIZE } : {}) }}
        >
          {model.title}
        </Typography>
        {/* The container's owner and kind, trailing the title like a tree row's
            chips (container-view merge): they read in one glance instead of
            taking a line of their own below the list. The owner is outlined so
            it stays quieter than the filled kind chip. */}
        {model.kind && (
          <ContainerMetaChips
            owner={model.owner}
            kind={model.kind}
            sx={{ flexShrink: 0, mt: 0.5 }}
            ownerTestId="content-owner"
            kindTestId="content-kind"
          />
        )}
      </Box>

      {model.caption && (
        <Typography variant="body2" color="text.secondary" data-testid="content-caption" sx={{ mt: 0.25 }}>
          {model.caption}
        </Typography>
      )}

      {model.crumbs && model.crumbs.length > 1 && (
        <Breadcrumbs
          aria-label={t("breadcrumb")}
          // The phone clips the trail instead of wrapping it, but never to the
          // point where "up one level" disappears: first crumb, ellipsis, then
          // the last two (`All storage / … / Mitte / Spielsachen`).
          maxItems={compact ? 4 : undefined}
          itemsBeforeCollapse={1}
          itemsAfterCollapse={2}
          sx={{
            mt: 0.5,
            // The phone keeps the strip on one line; tablet/desktop may wrap.
            "& .MuiBreadcrumbs-ol": { flexWrap: compact ? "nowrap" : "wrap" },
          }}
        >
          {model.crumbs.map((crumb, index) =>
            index === model.crumbs!.length - 1 && !linkLastCrumb ? (
              <Typography
                key={crumb.id ?? "root"}
                color="text.primary"
                variant="body2"
                data-testid={`crumb-${crumb.id ?? "root"}`}
              >
                {crumb.label}
              </Typography>
            ) : (
              <CrumbButton
                key={crumb.id ?? "root"}
                crumb={crumb}
                onCrumb={onCrumb}
              />
            ),
          )}
        </Breadcrumbs>
      )}

      {/* The container's identity belongs with the header, directly under the
          trail: the short UUID, copy and QR are how a physical box is matched
          to this page (ADR-0027). */}
      {model.identity && (
        <Box sx={{ mt: 0.5 }} data-testid="content-identity">
          <IdentityLine uuid={model.identity.uuid} name={model.identity.name} />
        </Box>
      )}
    </Box>
  );
}

/** An ancestor crumb: 44px hit area, compact visual size (spec §10). */
function CrumbButton({
  crumb,
  onCrumb,
}: {
  crumb: Crumb;
  onCrumb: (containerId: string | null) => void;
}) {
  const label = crumb.label;
  return (
    <ButtonBase
      onClick={() => onCrumb(crumb.id)}
      data-testid={`crumb-${crumb.id ?? "root"}`}
      sx={{
        minHeight: 44,
        px: 0.5,
        borderRadius: 1,
        color: "text.secondary",
        fontSize: (theme) => theme.typography.body2.fontSize,
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {label}
    </ButtonBase>
  );
}
